import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { QUERY_STALE_TIME, isSupabaseConfigured } from '@/config';
import { useAuth } from '@/lib/auth/AuthProvider';
import {
  getAccaLeaderboard,
  getAccaState,
  recordMissionEvent,
  removeAccaPick,
  submitAccaPick,
  type AccaSelection,
} from '@/lib/data/provider';

/** The active daily Acca card: the day's fixtures + the caller's picks + result. */
export function useAccaState() {
  const { userId } = useAuth();
  return useQuery({
    queryKey: ['acca', 'state', userId ?? 'anon'],
    queryFn: getAccaState,
    staleTime: QUERY_STALE_TIME.live,
    enabled: isSupabaseConfigured,
  });
}

export function useSubmitAccaPick() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { fixtureId: string; selection: AccaSelection }) =>
      submitAccaPick(input.fixtureId, input.selection),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['acca'] });
      void recordMissionEvent('acca')
        .then(() => queryClient.invalidateQueries({ queryKey: ['rewards'] }))
        .catch(() => {});
    },
  });
}

export function useRemoveAccaPick() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fixtureId: string) => removeAccaPick(fixtureId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['acca'] }),
  });
}

export function useAccaLeaderboard(enabled = true) {
  return useQuery({
    queryKey: ['acca', 'leaderboard'],
    queryFn: () => getAccaLeaderboard(),
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && enabled,
  });
}
